import { useCallback, useEffect, useMemo, useState } from "react";
import { sendOTPRequest } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";
import { addEmail, addPhone } from "features/user/userSlice";
import i18n from "i18n";
import {
  getCustomerLoginMode,
  getLoginFieldContent,
  getLoginPayload,
  resolvePrefilledLoginPayload,
} from "./loginFieldConfig";
import {
  requiresTrackingPhoneConfirmation,
} from "utils/trackingAuth";

function useLoginFlow({
  rmsConfig,
  params,
  encipherencode,
  dispatch,
  setValue,
  setLoginLoader,
}) {
  const [confirmOtp, setConfirmOtp] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [response, setResponse] = useState(null);
  const [otpSendError, setOtpSendError] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const userPhone = params.get("p");
  const userEmail = params.get("e");

  const loginMode = useMemo(
    () => getCustomerLoginMode(rmsConfig.customer_verification_method),
    [rmsConfig.customer_verification_method]
  );
  const loginFieldContent = useMemo(
    () => getLoginFieldContent(loginMode),
    [loginMode]
  );

  const submitLoginPayload = useCallback(
    (loginPayload) => {
      if (!loginPayload) {
        return;
      }

      dispatch(addPhone(loginPayload.phone));
      dispatch(addEmail(loginPayload.email));
      setInputValue(loginPayload.inputValue);

      setOtpSendError(false);
      setOtpLoading(true);
      sendOTPRequest(loginPayload.email, encipherencode, loginPayload.phone)
        .then((resp) => {
          console.debug("Login OTP request succeeded", {
            hasResponseData: Boolean(resp?.data),
            loginMode,
            hasEmail: Boolean(loginPayload.email),
            hasPhone: Boolean(loginPayload.phone),
          });
          setResponse(resp.data);
          console.debug("Login flow switching to OTP screen");
          setConfirmOtp(true);
        })
        .catch((error) => {
          console.error("Login OTP request failed", {
            error,
            loginMode,
            hasEmail: Boolean(loginPayload.email),
            hasPhone: Boolean(loginPayload.phone),
          });
          setOtpSendError(getApiErrorMessage(error, i18n.t("login.otpRetry")));
        })
        .finally(() => {
          setOtpLoading(false);
        });
    },
    [dispatch, encipherencode, loginMode]
  );

  useEffect(() => {
    const loaderTimeout = setTimeout(() => {
      setLoginLoader(false);
    }, 750);

    const prefilledLoginPayload = resolvePrefilledLoginPayload({
      loginMode,
      userPhone,
      userEmail,
    });

    if (prefilledLoginPayload) {
      setValue("customerIdentifier", prefilledLoginPayload.inputValue);
      if (!requiresTrackingPhoneConfirmation(prefilledLoginPayload.phone)) {
        submitLoginPayload(prefilledLoginPayload);
      }
    }

    return () => {
      clearTimeout(loaderTimeout);
    };
  }, [
    loginMode,
    setLoginLoader,
    setValue,
    submitLoginPayload,
    userEmail,
    userPhone,
  ]);

  const loginHandler = useCallback(
    ({ customerIdentifier }) => {
      submitLoginPayload(getLoginPayload(customerIdentifier, loginMode));
    },
    [loginMode, submitLoginPayload]
  );

  return {
    confirmOtp,
    setConfirmOtp,
    inputValue,
    response,
    setResponse,
    otpSendError,
    otpLoading,
    loginFieldContent,
    loginHandler,
  };
}

export default useLoginFlow;
